import { useState, useEffect, useRef } from "react";

function relativeTime(ts) {
  if (!ts) return "";
  const date = new Date(ts);
  if (isNaN(date)) return ts;
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// Instantly provider_code (developer.instantly.ai, create-account): 1 custom IMAP/SMTP, 2 Google, 3 Microsoft.
// MAILBOX-DOMAIN-1: before this the modal sent 2 for every provider.
const PROVIDER_CODE = { google: 2, microsoft: 3, custom: 1 };
const PROVIDER_HOSTS = {
  google: { smtp_host: 'smtp.gmail.com', smtp_port: 587, imap_host: 'imap.gmail.com', imap_port: 993 },
  microsoft: { smtp_host: 'smtp.office365.com', smtp_port: 587, imap_host: 'outlook.office365.com', imap_port: 993 },
};

export default function CRMView({ API, currentUser }) {
  const [replies, setReplies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);
  const [sending, setSending] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [hoveredReply, setHoveredReply] = useState(null);
  const [showSignaturePanel, setShowSignaturePanel] = useState(false);
  const [signature, setSignature] = useState('');
  const [draftSignature, setDraftSignature] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);
  const composerRef = useRef(null);
  const threadRef = useRef(null);
  const [crmTab, setCrmTab] = useState('inbox');
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [showAddEmail, setShowAddEmail] = useState(false);
  const [addEmailForm, setAddEmailForm] = useState({
    email: '', first_name: '', last_name: '', password: '',
    provider: 'google', smtp_host: 'smtp.gmail.com', smtp_port: 587,
    imap_host: 'imap.gmail.com', imap_port: 993,
  });
  const [addEmailLoading, setAddEmailLoading] = useState(false);
  const [addEmailError, setAddEmailError] = useState('');
  const [enabledAccounts, setEnabledAccounts] = useState({});
  const [signatureSaved, setSignatureSaved] = useState(false);
  const [signatureError, setSignatureError] = useState('');
  const [includeSignature, setIncludeSignature] = useState(true);
  const [thread, setThread] = useState([]);

  useEffect(() => {
    fetchReplies();
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (!selected) return;
    const token = localStorage.getItem('expertnet_token');
    fetch(`${API}/crm/thread/${selected.expert_id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        setThread(data.messages || []);
        setTimeout(() => {
          if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
        }, 0);
      })
      .catch(() => setThread([]));
  }, [selected]);

  const fetchAccounts = async () => {
    setAccountsLoading(true);
    try {
      const token = localStorage.getItem('expertnet_token');
      const res = await fetch(`${API}/instantly/accounts`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setAccounts(d.accounts || []); }
    } catch(e) {}
    const pref = await fetch(`${API}/user-email-accounts/${currentUser?.id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` } }).then(r => r.json()).catch(() => ({ accounts: [] }));
    const map = {};
    (pref.accounts || []).forEach(a => { map[a.email] = a.enabled; });
    setEnabledAccounts(map);
    setAccountsLoading(false);
  };

  const toggleAccount = async (email) => {
    const newVal = !(enabledAccounts[email] !== false);
    setEnabledAccounts(prev => ({ ...prev, [email]: newVal }));
    await fetch(`${API}/user-email-accounts/${currentUser?.id}/toggle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
      body: JSON.stringify({ email, enabled: newVal })
    });
  };

  useEffect(() => {
    if (currentUser?.id) {
      fetch(`${API}/users/${currentUser.id}/signature`, { headers: { Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` } })
        .then(r => r.json())
        .then(data => { if (data.signature) setSignature(data.signature); })
        .catch(() => {});
    }
  }, [currentUser]);

  useEffect(() => {
    if (crmTab === 'accounts') fetchAccounts();
    if (crmTab === 'settings' && currentUser?.id) {
      fetch(`${API}/users/${currentUser.id}/signature`, { headers: { Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` } })
        .then(r => r.json())
        .then(data => { if (data.signature) setSignature(data.signature); })
        .catch(() => {});
    }
  }, [crmTab]);

  const handleAddEmail = async () => {
    if (!addEmailForm.email || !addEmailForm.password) {
      setAddEmailError('Email and password are required'); return;
    }
    setAddEmailLoading(true); setAddEmailError('');
    try {
      const body = {
        email: addEmailForm.email,
        first_name: addEmailForm.first_name,
        last_name: addEmailForm.last_name,
        provider_code: PROVIDER_CODE[addEmailForm.provider] || 1,
        smtp_username: addEmailForm.email,
        smtp_password: addEmailForm.password,
        smtp_host: addEmailForm.smtp_host,
        smtp_port: Number(addEmailForm.smtp_port),
        imap_username: addEmailForm.email,
        imap_password: addEmailForm.password,
        imap_host: addEmailForm.imap_host,
        imap_port: Number(addEmailForm.imap_port),
      };
      const res = await fetch(`${API}/instantly/accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (res.ok) {
        setShowAddEmail(false);
        setAddEmailForm({ email: '', first_name: '', last_name: '', password: '', provider: 'google', smtp_host: 'smtp.gmail.com', smtp_port: 587, imap_host: 'imap.gmail.com', imap_port: 993 });
        fetchAccounts();
      } else {
        setAddEmailError(d.error || 'Failed to add account');
      }
    } catch(e) { setAddEmailError('Something went wrong'); }
    setAddEmailLoading(false);
  };

  const fetchReplies = async () => {
    try {
      const res = await fetch(`${API}/crm/replies`, { headers: { Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` } });
      if (res.ok) setReplies(await res.json());
    } catch (e) {
      console.error("Failed to fetch replies:", e);
    }
  };

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const selectReply = async (reply) => {
    setComposerOpen(false);
    setSelected(reply);

    if (!reply.is_read) {
      setReplies(prev => prev.map(r => r.id === reply.id ? { ...r, is_read: 1 } : r));
      setSelected(prev => prev && prev.id === reply.id ? { ...prev, is_read: 1 } : prev);
      try {
        await fetch(`${API}/crm/replies/${reply.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
          body: JSON.stringify({ is_read: 1 }),
        });
      } catch (e) {
        console.error("Failed to mark as read:", e);
      }
    }
  };

  const archiveReply = async (e, reply) => {
    e.stopPropagation();
    setReplies(prev => prev.map(r => r.id === reply.id ? { ...r, is_archived: 1 } : r));
    try {
      await fetch(`${API}/crm/replies/${reply.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify({ is_archived: 1 }),
      });
    } catch (e) {
      console.error("Failed to archive reply:", e);
    }
  };

  const applyFormat = (tag) => {
    const composer = composerRef.current;
    if (!composer) return;
    composer.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    const el = document.createElement(tag);
    try {
      range.surroundContents(el);
    } catch {
      el.appendChild(range.extractContents());
      range.insertNode(el);
    }
    sel.removeAllRanges();
  };

  // SIG-SAVE-1: one save path for both editors (Settings tab and the Inbox drawer).
  // Returns true only when the server accepted the signature; the pitch path reads
  // users.email_signature, so a signature that lives only in page state is not sent.
  const saveSignature = async (value) => {
    const sig = value === undefined ? signature : value;
    setSignatureError('');
    try {
      const res = await fetch(`${API}/users/${currentUser.id}/signature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify({ signature: sig })
      });
      if (!res.ok) {
        setSignatureError('Signature did not save. Try again.');
        return false;
      }
    } catch (e) {
      setSignatureError('Signature did not save. Try again.');
      return false;
    }
    setSignature(sig);
    setSignatureSaved(true);
    setTimeout(() => setSignatureSaved(false), 2000);
    return true;
  };

  const sendReply = async () => {
    if (!selected || !composerRef.current) return;
    let body = composerRef.current.innerHTML.trim();
    if (includeSignature && signature) {
      body = body + '<br><br>' + signature.replace(/\n/g, '<br>');
    }
    if (!body) { showToast("Reply body is empty", "error"); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/crm/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify({
          eaccount: selected.eaccount,
          reply_to_uuid: selected.instantly_email_id,
          subject: "Re: " + (selected.subject || ""),
          body,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        showToast("Reply sent!");
        composerRef.current.innerHTML = "";
        setComposerOpen(false);
        setSelected(prev => ({ ...prev }));
        setReplies(prev => prev.map(r => r.id === selected.id ? { ...r, is_read: 1 } : r));
      } else {
        showToast(data.error || "Failed to send reply", "error");
      }
    } catch (e) {
      showToast("Failed to send reply", "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>

      {/* Tab bar */}
      <div style={{ borderBottom: "1px solid var(--line)", padding: "0 24px", display: "flex", alignItems: "center", flexShrink: 0 }}>
        {['inbox', 'accounts', 'settings'].map(t => (
          <button
            key={t}
            onClick={() => setCrmTab(t)}
            style={{
              padding: "12px 16px", fontSize: 13, fontWeight: 500,
              background: "transparent", border: "none", cursor: "pointer",
              textTransform: "capitalize",
              color: crmTab === t ? "var(--text-1)" : "var(--text-3)",
              borderBottom: crmTab === t ? "2px solid var(--accent)" : "2px solid transparent",
              marginBottom: -1,
            }}
          >{t === 'inbox' ? 'Inbox' : t === 'accounts' ? 'Accounts' : 'Settings'}</button>
        ))}
        {accounts.length > 0 && (() => {
          const totalSent  = accounts.reduce((s, a) => s + (a.sent_today || 0), 0);
          const totalLimit = accounts.reduce((s, a) => s + (a.daily_limit || 0), 0);
          const ratio = totalLimit > 0 ? totalSent / totalLimit : 0;
          const color = ratio >= 0.9 ? 'var(--negative)' : ratio >= 0.7 ? 'var(--warning)' : 'var(--text-2)';
          return (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}
              title={`Across ${accounts.length} mailbox${accounts.length === 1 ? '' : 'es'}`}>
              <span className="r-eyebrow">Email</span>
              <span style={{ fontFamily: 'var(--font-data)', fontSize: 12, color }}>
                {totalSent}/{totalLimit} today
              </span>
            </div>
          );
        })()}
      </div>

      {crmTab === 'inbox' && (
      <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>

        {/* Inbox Intelligence strip */}
        <div style={{ padding: '18px 24px 0' }}>
          <div style={{
            borderRadius: 12, border: '1px solid rgba(45,212,191,.35)',
            background: 'linear-gradient(90deg, rgba(14,116,144,.38), rgba(45,212,191,.10) 45%, rgba(10,22,40,.6))',
            backdropFilter: 'var(--glass-blur)', WebkitBackdropFilter: 'var(--glass-blur)',
            padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
          }}>
            {/* 28px gradient square with pulsing dot */}
            <div style={{
              width: 28, height: 28, borderRadius: 8, flexShrink: 0,
              background: 'linear-gradient(135deg,#5eead4,#14b8a6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', animation: 'rasp-pulse 2.4s ease-in-out infinite', display: 'block' }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                <span className="r-eyebrow" style={{ color: 'var(--accent)' }}>Inbox Intelligence</span>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--positive)', display: 'inline-block', flexShrink: 0 }} />
                <span style={{ fontFamily: 'var(--font-data)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-4)' }}>LIVE</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
                <span style={{ fontFamily: 'var(--font-data)', color: 'var(--text-1)' }}>{replies.filter(r => r.unread_count > 0 && !r.is_archived).length}</span>
                {' unread · '}
                <span style={{ fontFamily: 'var(--font-data)', color: 'var(--text-1)' }}>{replies.filter(r => !r.is_archived).length}</span>
                {' total replies'}
              </div>
            </div>
            <button
              className="r-btn r-btn-ghost"
              style={{ height: 28 }}
              onClick={() => { setDraftSignature(signature); setShowSignaturePanel(true); }}
            >Signature</button>
          </div>
        </div>

        {/* Two panes */}
        <div style={{ display: "flex", flexWrap: "nowrap", gap: 14, padding: "14px 24px 24px", flex: 1, minHeight: 0 }}>

          {/* List pane */}
          <div className="r-card" style={{ flex: "1 1 300px", maxWidth: 380, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            {/* List header */}
            <div style={{ height: 42, padding: "0 14px", borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-1)" }}>Replies</span>
              <span style={{ fontFamily: "var(--font-data)", fontSize: 11, color: "var(--text-4)" }}>
                {replies.filter(r => showArchived ? r.is_archived : !r.is_archived).length}
              </span>
            </div>
            {/* List body */}
            <div style={{ flex: 1, overflowY: "auto" }}>
              {(() => {
                const visibleReplies = replies.filter(r => showArchived ? r.is_archived : !r.is_archived);
                const archivedCount = replies.filter(r => r.is_archived).length;
                return (
                  <>
                    {visibleReplies.length === 0 ? (
                      <div style={{ padding: "48px 24px", textAlign: "center", color: "var(--text-4)", fontSize: 13 }}>
                        {showArchived ? "No archived replies." : "No replies yet. Replies from your outreach campaigns will appear here."}
                      </div>
                    ) : (
                      visibleReplies.map(reply => {
                        const isSelected = selected?.id === reply.id;
                        const unread = reply.unread_count > 0;
                        const isHovered = hoveredReply === reply.id;
                        return (
                          <div
                            key={reply.id}
                            onClick={() => selectReply(reply)}
                            onMouseEnter={() => setHoveredReply(reply.id)}
                            onMouseLeave={() => setHoveredReply(null)}
                            style={{
                              display: "grid", gridTemplateColumns: "14px 1fr auto",
                              gap: "0 8px",
                              padding: "10px 14px",
                              borderBottom: "1px solid var(--line-soft)",
                              cursor: "pointer",
                              background: isSelected ? "rgba(45,212,191,.08)" : "transparent",
                              boxShadow: isSelected ? "inset 2px 0 0 var(--accent)" : "none",
                              transition: "background 0.12s",
                            }}
                            onMouseOver={e => { if (!isSelected) e.currentTarget.style.background = "var(--row-hover)"; }}
                            onMouseOut={e => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                          >
                            {/* Col 1: unread dot */}
                            <div style={{ display: "flex", alignItems: "flex-start", paddingTop: 4 }}>
                              {unread && <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--accent)", flexShrink: 0, display: "block" }} />}
                            </div>
                            {/* Col 2: name / project / subject */}
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: unread ? 600 : 500, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {reply.expert_name || reply.from_email || "Unknown"}
                              </div>
                              <div style={{ fontSize: 11, color: reply.project_name ? "var(--accent)" : "var(--text-5)", marginTop: 1 }}>
                                {reply.project_name || "—"}
                              </div>
                              <div style={{ fontSize: 12, color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>
                                {reply.subject || "(no subject)"}
                              </div>
                            </div>
                            {/* Col 3: time + archive */}
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                              <span style={{ fontFamily: "var(--font-data)", fontSize: 11, color: "var(--text-4)" }}>
                                {relativeTime(reply.timestamp_email)}
                              </span>
                              {isHovered && !showArchived && (
                                <button
                                  onClick={(e) => archiveReply(e, reply)}
                                  title="Archive"
                                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "var(--font-data)", fontSize: 10, color: "var(--text-4)", lineHeight: 1 }}
                                  onMouseOver={e => e.currentTarget.style.color = "var(--text-1)"}
                                  onMouseOut={e => e.currentTarget.style.color = "var(--text-4)"}
                                >Archive</button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                    {!showArchived && (
                      <div
                        onClick={() => setShowArchived(true)}
                        style={{ padding: "10px 14px", cursor: "pointer", fontSize: 12, color: "var(--text-4)", borderTop: "1px solid var(--line-soft)", textAlign: "center" }}
                        onMouseOver={e => e.currentTarget.style.color = "var(--text-1)"}
                        onMouseOut={e => e.currentTarget.style.color = "var(--text-4)"}
                      >
                        Archived ({archivedCount})
                      </div>
                    )}
                    {showArchived && (
                      <div
                        onClick={() => setShowArchived(false)}
                        style={{ padding: "10px 14px", cursor: "pointer", fontSize: 12, color: "var(--accent)", borderTop: "1px solid var(--line-soft)", textAlign: "center" }}
                      >
                        ← Back to Inbox
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          {/* Reader pane */}
          <div className="r-card" style={{ flex: "3 1 340px", minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            {!selected ? (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <span style={{ fontSize: 28, color: "var(--text-5)" }}>✉</span>
                <span style={{ fontSize: 13, color: "var(--text-4)" }}>Select a reply to view</span>
              </div>
            ) : (
              <>
                {/* Reader header */}
                <div style={{ padding: "18px 20px", borderBottom: "1px solid var(--line)", flexShrink: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-1)", marginBottom: 4 }}>
                    {selected.subject || selected.expert_name || selected.from_email}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                    {selected.expert_name || selected.from_email}
                    {selected.project_name && <span> · {selected.project_name}</span>}
                    {' · '}<span style={{ fontFamily: "var(--font-data)" }}>{selected.from_email}</span>
                  </div>
                </div>

                {/* Thread messages */}
                <div ref={threadRef} style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", borderBottom: "1px solid var(--line-soft)" }}>
                  {thread.length > 0 ? thread.map((msg, i) => (
                    <div key={i} style={{ padding: "14px 20px", borderBottom: i < thread.length - 1 ? "1px solid var(--line-soft)" : "none" }}>
                      <div style={{ fontSize: 11, color: msg.is_outbound ? "var(--accent)" : "var(--text-3)", marginBottom: 6 }}>
                        {msg.is_outbound ? "You" : (selected.expert_name || selected.from_email)}
                        {' · '}
                        <span style={{ fontFamily: "var(--font-data)" }}>{msg.timestamp_email ? new Date(msg.timestamp_email).toLocaleString() : ""}</span>
                      </div>
                      {msg.is_outbound ? (
                        <div className="crm-body" style={{ padding: "10px 14px", background: "rgba(45,212,191,.08)", border: "1px solid rgba(45,212,191,.25)", borderRadius: 8, fontSize: 14, color: "var(--text-1)", lineHeight: 1.65, maxWidth: 760, overflowWrap: "anywhere" }} dangerouslySetInnerHTML={{ __html: msg.body || "" }} />
                      ) : (
                        <div className="crm-body" style={{ fontSize: 14, color: "var(--text-2)", lineHeight: 1.65, maxWidth: 760, overflowWrap: "anywhere" }} dangerouslySetInnerHTML={{ __html: msg.body || "" }} />
                      )}
                    </div>
                  )) : (
                    <div style={{ padding: "14px 20px" }}>
                      <div className="crm-body" style={{ fontSize: 14, color: "var(--text-2)", lineHeight: 1.65, maxWidth: 760, overflowWrap: "anywhere" }} dangerouslySetInnerHTML={{ __html: selected.body || '<em style="color:var(--text-4)">No body content.</em>' }} />
                    </div>
                  )}
                </div>

                {/* Composer */}
                {!composerOpen ? (
                  <div style={{ padding: "10px 20px 14px", flexShrink: 0 }}>
                    <button className="r-btn r-btn-primary" style={{ height: 28 }} onClick={() => setComposerOpen(true)}>Reply</button>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", padding: "14px 20px 18px", flexShrink: 0 }}>
                    <span className="r-eyebrow" style={{ color: "var(--accent)", marginBottom: 10 }}>✦ Rasp AI · compose reply</span>

                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <input type="checkbox" id="sig-toggle" checked={includeSignature} onChange={e => setIncludeSignature(e.target.checked)} style={{ accentColor: "var(--accent)" }} />
                      <label htmlFor="sig-toggle" style={{ fontSize: 12, color: "var(--text-3)", cursor: "pointer" }}>Include signature</label>
                    </div>

                    {/* Format toolbar */}
                    <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                      {[["B", "b"], ["I", "i"]].map(([label, tag]) => (
                        <button
                          key={tag}
                          className="r-btn r-btn-ghost"
                          onMouseDown={(e) => { e.preventDefault(); applyFormat(tag); }}
                          style={{ width: 26, height: 26, padding: 0, fontSize: 12, fontWeight: 700, fontStyle: tag === "i" ? "italic" : "normal" }}
                        >{label}</button>
                      ))}
                    </div>

                    {/* Contenteditable box */}
                    <div
                      ref={composerRef}
                      contentEditable
                      suppressContentEditableWarning
                      style={{
                        minHeight: 88, padding: "10px 12px",
                        background: "var(--input-bg)", border: "1px solid var(--line)",
                        borderRadius: 8, color: "var(--text-1)", fontSize: 13, lineHeight: 1.6,
                        overflowY: "auto", outline: "none",
                      }}
                      onFocus={e => { e.currentTarget.style.borderColor = "rgba(45,212,191,.5)"; }}
                      onBlur={e => { e.currentTarget.style.borderColor = "var(--line)"; }}
                    />

                    <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
                      <button className="r-btn r-btn-ghost" style={{ height: 28 }} onClick={() => setComposerOpen(false)}>Cancel</button>
                      <button
                        className="r-btn r-btn-primary"
                        onClick={sendReply}
                        disabled={sending}
                        style={{ height: 28, ...(sending ? { opacity: 0.6, cursor: "not-allowed" } : {}) }}
                      >
                        {sending ? "Sending..." : "Send Reply"}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

        </div>
      </div>
      )}

      {crmTab === 'accounts' && (
        <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <button className="r-btn r-btn-primary" onClick={() => setShowAddEmail(true)}>Add Email Account</button>
          </div>
          {accountsLoading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
              <span style={{ color: "var(--text-3)", fontSize: 13 }}>Loading...</span>
            </div>
          ) : accounts.length === 0 ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
              <span style={{ color: "var(--text-3)", fontSize: 13 }}>No email accounts connected.</span>
            </div>
          ) : (
            <div className="r-card">
              {accounts.map((acct, idx) => {
                const pct = acct.daily_limit > 0 ? (acct.sent_today / acct.daily_limit) * 100 : 0;
                const fillColor = acct.sent_today === 0 ? 'var(--positive)' : pct >= 100 ? 'var(--negative)' : 'var(--accent)';
                return (
                  <div key={acct.email} style={{
                    display: "flex", alignItems: "center", height: 56,
                    padding: "0 18px",
                    borderBottom: idx < accounts.length - 1 ? "1px solid var(--line-soft)" : "none",
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: "var(--font-data)", fontSize: 12, color: "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{acct.email}</div>
                      <div style={{ width: "100%", height: 3, background: "var(--line-soft)", borderRadius: 2, marginTop: 4 }}>
                        <div style={{ width: `${Math.min(pct, 100)}%`, height: "100%", background: fillColor, borderRadius: 2 }} />
                      </div>
                    </div>
                    <div style={{ marginLeft: 20, textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontFamily: "var(--font-data)", fontSize: 12 }}>
                        <span style={{ color: "var(--accent)" }}>{acct.sent_today}</span>
                        <span style={{ color: "var(--text-4)" }}> / {acct.daily_limit}</span>
                      </div>
                      {acct.health_score != null && (
                        <div style={{ fontFamily: "var(--font-data)", fontSize: 11, color: "var(--text-4)", marginTop: 2 }}>{acct.health_score}%</div>
                      )}
                    </div>
                    <div
                      onClick={() => toggleAccount(acct.email)}
                      style={{ marginLeft: 16, width: 34, height: 20, borderRadius: 10, background: enabledAccounts[acct.email] !== false ? 'rgba(45,212,191,.6)' : 'rgba(255,255,255,.12)', position: 'relative', cursor: 'pointer', flexShrink: 0, transition: 'background 0.2s' }}
                    >
                      <div style={{ position: 'absolute', top: 2, left: enabledAccounts[acct.email] !== false ? 16 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {crmTab === 'settings' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          <div className="r-card" style={{ padding: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)', marginBottom: 6 }}>Email Signature</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 14 }}>Added to the bottom of every outreach email.</div>
            <textarea
              value={signature}
              onChange={e => setSignature(e.target.value)}
              style={{ width: '100%', height: 120, background: 'var(--input-bg)', border: '1px solid var(--line)', borderRadius: 8, color: 'var(--text-1)', fontSize: 13, padding: 12, boxSizing: 'border-box', resize: 'vertical', outline: 'none' }}
            />
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
              <button className="r-btn r-btn-primary" data-testid="sig-settings-save" onClick={() => saveSignature()}>Save Signature</button>
              {signatureSaved && <span style={{ color: 'var(--positive)', fontSize: 12 }}>Saved</span>}
              {signatureError && <span data-testid="sig-settings-error" style={{ color: 'var(--negative)', fontSize: 12 }}>{signatureError}</span>}
            </div>
          </div>
        </div>
      )}

      {/* Add Email Account Modal */}
      {showAddEmail && (() => {
        const modalInput = {
          width: "100%", background: "var(--input-bg)", border: "1px solid var(--line)",
          borderRadius: 8, color: "var(--text-1)", fontSize: 13, height: 34, padding: "0 12px",
          outline: "none", boxSizing: "border-box", marginBottom: 14,
        };
        const isCustom = addEmailForm.provider === 'custom';
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(2,7,13,.72)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
            <div style={{ background: "var(--glass-card)", border: "1px solid var(--line)", borderRadius: 12, padding: 28, maxWidth: 480, width: "100%", boxShadow: "var(--card-shadow)", backdropFilter: "var(--glass-blur)", WebkitBackdropFilter: "var(--glass-blur)" }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text-1)", marginBottom: 20 }}>Add Email Account</div>

              <div className="r-eyebrow" style={{ marginBottom: 6 }}>Email Address</div>
              <input type="email" style={modalInput} value={addEmailForm.email} onChange={e => setAddEmailForm(f => ({ ...f, email: e.target.value }))} />

              <div className="r-eyebrow" style={{ marginBottom: 6 }}>First Name</div>
              <input style={modalInput} value={addEmailForm.first_name} onChange={e => setAddEmailForm(f => ({ ...f, first_name: e.target.value }))} />

              <div className="r-eyebrow" style={{ marginBottom: 6 }}>Last Name</div>
              <input style={modalInput} value={addEmailForm.last_name} onChange={e => setAddEmailForm(f => ({ ...f, last_name: e.target.value }))} />

              <div className="r-eyebrow" style={{ marginBottom: 6 }}>App Password</div>
              <input type="password" style={modalInput} value={addEmailForm.password} onChange={e => setAddEmailForm(f => ({ ...f, password: e.target.value }))} />
              <div style={{ color: "var(--text-4)", fontSize: 11, marginTop: -10, marginBottom: 14 }}>
                {addEmailForm.provider === 'google'
                  ? 'Gmail: turn on 2-Step Verification, then create an App Password (Google Account → Security). Your normal password will not work.'
                  : addEmailForm.provider === 'microsoft'
                    ? 'Outlook / Microsoft 365: use an App Password, and IMAP must be enabled on the mailbox.'
                    : 'Use the password your mail provider gives for IMAP/SMTP access.'}
              </div>

              <div style={{ marginTop: 12, marginBottom: 16 }}>
                <div className="r-eyebrow" style={{ marginBottom: 8 }}>Provider</div>
                <div className="r-seg" style={{ display: "inline-flex", gap: 2 }}>
                  {[['google', 'Google / Gmail'], ['microsoft', 'Microsoft / Outlook'], ['custom', 'Custom SMTP']].map(([val, label]) => (
                    <button
                      key={val}
                      onClick={() => setAddEmailForm(f => ({
                        ...f,
                        provider: val,
                        ...(PROVIDER_HOSTS[val] || {}),
                      }))}
                      className={addEmailForm.provider === val ? 'r-seg-on' : ''}
                      style={{ padding: "5px 12px", fontSize: 12, fontWeight: 500, cursor: "pointer", border: "none", background: "transparent", color: addEmailForm.provider === val ? "var(--text-1)" : "var(--text-3)", borderRadius: 6 }}
                    >{label}</button>
                  ))}
                </div>
              </div>

              {isCustom && (
                <>
                  <div className="r-eyebrow" style={{ marginBottom: 6 }}>SMTP Host</div>
                  <input style={modalInput} value={addEmailForm.smtp_host} onChange={e => setAddEmailForm(f => ({ ...f, smtp_host: e.target.value }))} />
                  <div className="r-eyebrow" style={{ marginBottom: 6 }}>SMTP Port</div>
                  <input type="number" style={modalInput} value={addEmailForm.smtp_port} onChange={e => setAddEmailForm(f => ({ ...f, smtp_port: e.target.value }))} />
                  <div className="r-eyebrow" style={{ marginBottom: 6 }}>IMAP Host</div>
                  <input style={modalInput} value={addEmailForm.imap_host} onChange={e => setAddEmailForm(f => ({ ...f, imap_host: e.target.value }))} />
                  <div className="r-eyebrow" style={{ marginBottom: 6 }}>IMAP Port</div>
                  <input type="number" style={modalInput} value={addEmailForm.imap_port} onChange={e => setAddEmailForm(f => ({ ...f, imap_port: e.target.value }))} />
                </>
              )}

              {addEmailError && <div style={{ color: "var(--negative)", fontSize: 12, marginBottom: 12 }}>{addEmailError}</div>}

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
                <button
                  className="r-btn r-btn-ghost"
                  onClick={() => { setShowAddEmail(false); setAddEmailError(''); }}
                >Cancel</button>
                <button
                  className="r-btn r-btn-primary"
                  onClick={handleAddEmail}
                  disabled={addEmailLoading}
                  style={addEmailLoading ? { opacity: 0.7, cursor: "not-allowed" } : {}}
                >{addEmailLoading ? "Adding..." : "Add Account"}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Signature Settings Drawer */}
      {showSignaturePanel && (
        <>
          <div
            onClick={() => setShowSignaturePanel(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(2,7,13,.72)", zIndex: 1000 }}
          />
          <div style={{
            position: "fixed", top: 0, right: 0, bottom: 0, width: 360,
            background: "var(--glass-card)", borderLeft: "1px solid var(--line)",
            backdropFilter: "var(--glass-blur)", WebkitBackdropFilter: "var(--glass-blur)",
            display: "flex", flexDirection: "column", zIndex: 1001,
            boxShadow: "-4px 0 24px rgba(0,0,0,0.4)",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 18px 14px", borderBottom: "1px solid var(--line)" }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: "var(--text-1)" }}>Inbox Settings</span>
              <button
                className="r-btn r-btn-ghost"
                onClick={() => setShowSignaturePanel(false)}
                aria-label="Close"
                style={{ width: 24, height: 24, padding: 0, fontSize: 13 }}
              >✕</button>
            </div>
            <div style={{ flex: 1, padding: "18px", display: "flex", flexDirection: "column", gap: 12 }}>
              <span className="r-eyebrow">Email Signature</span>
              <textarea
                value={draftSignature}
                onChange={e => setDraftSignature(e.target.value)}
                placeholder="e.g. Best,&#10;Cameron Hinkle"
                rows={6}
                style={{
                  background: "var(--input-bg)", border: "1px solid var(--line)",
                  borderRadius: 8, color: "var(--text-1)", fontSize: 13, lineHeight: 1.6,
                  padding: "10px 12px", resize: "vertical", outline: "none",
                }}
                onFocus={e => { e.currentTarget.style.borderColor = "rgba(45,212,191,.5)"; }}
                onBlur={e => { e.currentTarget.style.borderColor = "var(--line)"; }}
              />
              {signatureError && <div data-testid="sig-drawer-error" style={{ color: "var(--negative)", fontSize: 12 }}>{signatureError}</div>}
              <button
                className="r-btn r-btn-primary"
                data-testid="sig-drawer-save"
                onClick={async () => { if (await saveSignature(draftSignature)) setShowSignaturePanel(false); }}
                style={{ alignSelf: "flex-end" }}
              >Save</button>
            </div>
          </div>
        </>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, right: 24,
          background: "var(--glass-card)", border: "1px solid var(--line)",
          color: toast.type === "error" ? "var(--negative)" : "var(--positive)",
          padding: "10px 18px", borderRadius: 8, fontSize: 13,
          zIndex: 9999, boxShadow: "var(--card-shadow)",
          backdropFilter: "var(--glass-blur)", WebkitBackdropFilter: "var(--glass-blur)",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <span className="r-dot" style={{ color: toast.type === "error" ? "var(--negative)" : "var(--positive)" }} />
          {toast.msg}
        </div>
      )}
    </div>
  );
}
